/**
 * The one place numbers become strings.
 *
 * 🔴 BUILD-BRIEF.md §7 records these rules as bugs that reached a paying
 * parent, and says to "enforce in a formatter utility, NOT per-component".
 * Components therefore take numbers and call these; none of them accepts a
 * pre-formatted string, because a rule the caller can bypass is a rule that
 * will be bypassed.
 *
 *   - Percentages are INTEGERS. A bar once rendered 58.99999999999997%.
 *   - Sibling values share ONE precision. 45.2% beside 42% reads as broken.
 *   - NO EMOJI INSIDE A NUMBER. A streak is a numeral with an icon beside it.
 *   - Tabular figures on every stat, so digits do not jitter as they update.
 */

/**
 * Unicode property escape rather than a hand-rolled codepoint range.
 *
 * The first version listed ranges including U+FE0F (variation selector) and
 * U+20E3 (combining enclosing keycap). Those are COMBINING marks, so putting
 * them in a character class matches half a grapheme — eslint's
 * no-misleading-character-class caught it. Extended_Pictographic is the
 * property that actually means "emoji", and it does not match the U+2212
 * minus sign that formatDelta emits.
 */
const EMOJI = /\p{Extended_Pictographic}/u;

/**
 * 🔴 A guard, not a formatter. Emoji belong in the label beside a number, never
 * inside it — `4 🔥` is the shape this exists to prevent.
 *
 * Throws in development so it surfaces in the component sheet; returns the
 * string in production rather than blanking a teacher's dashboard over a
 * cosmetic rule.
 */
export function assertNoEmojiInNumber(value: string): string {
  if (EMOJI.test(value)) {
    const message = `Emoji inside a number: "${value}". Put the emoji in the label beside it (design-system.md §12).`;
    if (import.meta.env.DEV) throw new Error(message);
    console.error(message);
  }
  return value;
}

/**
 * Integer percentage. 0–100, clamped.
 * `formatPercent(58.99999999999997)` → `"59%"`
 */
export function formatPercent(value: number): string {
  if (!Number.isFinite(value)) return "—";
  const clamped = Math.min(100, Math.max(0, value));
  return `${Math.round(clamped)}%`;
}

/** Integer count. Never a float, never a compact "1.2k" — a teacher wants the number. */
export function formatCount(value: number): string {
  if (!Number.isFinite(value)) return "—";
  return String(Math.round(value));
}

export interface DeltaLabels {
  /** Shown when the value is zero, e.g. "same as last week". */
  same: string;
  /** Appended to a signed number, e.g. "vs last week". */
  comparison: string;
}

/**
 * A week-over-week delta.
 *
 *   +3 → "+3 vs last week"
 *   -2 → "−2 vs last week"   (a real minus sign, U+2212, not a hyphen)
 *    0 → "same as last week"
 *
 * 🔴 Zero is a SENTENCE, not "+0". "A zero never contradicts the panel beside
 * it" — and "+0 vs last week" reads as a change when there was none.
 *
 * 🔴 THE WORDS ARE THE CALLER'S, THE NUMBER IS OURS.
 * The first version hardcoded its own wording here, which meant a sheet showing
 * "+3 vs last week" under a label that said something else — caught by looking
 * at it. A formatter that emits user-facing copy is one no screen can correct,
 * and one that would have to be rewritten rather than passed to if a second
 * language ever arrives.
 */
export function formatDelta(value: number, labels: DeltaLabels): string {
  if (!Number.isFinite(value)) return "—";
  const n = Math.round(value);
  if (n === 0) return labels.same;
  return `${n > 0 ? "+" : "−"}${Math.abs(n)} ${labels.comparison}`;
}

/**
 * 🔴 The level sentence. Content skills read as a LEVEL, never a percentage —
 * "a percentage where a level is the honest answer is a bug"
 * (design-system.md §8). A level is actionable; a percentage is not.
 *
 * The template is the caller's so it can be localised; the rounding is ours.
 * Tokens: {level} and {of}.
 *
 * 🔴 `of` IS REQUIRED, AND USED TO DEFAULT TO 5 — ADR-037.
 *
 * A default denominator is a copy of the level count living in the formatting
 * layer, where nobody reasoning about the content model would think to look for
 * it. It said 5 while the game shipped 3, so a parent read "Level 3 of 5" for a
 * child who had finished. Required means the number can only arrive from
 * `LEVEL_COUNT`, and a new caller has to decide rather than inherit.
 */
export function formatLevel(
  level: number,
  of: number,
  template = "Level {level} of {of}",
): string {
  return template
    .replace("{level}", String(Math.round(level)))
    .replace("{of}", String(Math.round(of)));
}

/**
 * Sibling values must share one precision. Every formatter above already
 * rounds to an integer, so this is a check rather than a transform: it exists
 * so a future formatter that takes a precision cannot quietly break the rule
 * for one tile in a row.
 */
export function assertSharedPrecision(values: readonly string[]): void {
  const decimals = new Set(
    values.map((v) => (v.split(".")[1] ?? "").replace(/\D+$/, "").length),
  );
  if (decimals.size > 1) {
    const message = `Sibling values do not share one precision: ${values.join(", ")} (design-system.md §4).`;
    if (import.meta.env.DEV) throw new Error(message);
    console.error(message);
  }
}
